<template>
  <div class="calculator" data-cy="price-calculator-modal">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        data-cy="price-calculator-dialog"
        class="calculator w-fit max-w-fit"
        :show-close-button="false"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle>Enter a number</SDialogTitle>
        </SDialogHeader>
        <div class="">
          <p class="input-screen" data-cy="price-calculator-display">
            <!-- <span class="currency">{{ currentCurrency }}</span> -->
            <span>{{ Number(inputContent).toFixed(2) }}</span>
          </p>
          <div class="calculator-buttons-wrapper">
            <div class="d-flex flex-row fl-ev">
              <p @click="clearInput()">C</p>
              <p @click="updateInput">{{ decimal }}</p>
              <p @click="eraseFromInput()">DEL</p>
            </div>
            <div class="d-flex flex-row fl-ev">
              <p @click="updateInput">7</p>
              <p @click="updateInput">8</p>
              <p @click="updateInput">9</p>
            </div>
            <div class="d-flex flex-row fl-ev">
              <p @click="updateInput">4</p>
              <p @click="updateInput">5</p>
              <p @click="updateInput">6</p>
            </div>
            <div class="d-flex flex-row fl-ev">
              <p @click="updateInput">1</p>
              <p @click="updateInput">2</p>
              <p @click="updateInput">3</p>
            </div>
            <div class="d-flex flex-row fl-ev">
              <p @click="updateInput">00</p>
              <p @click="updateInput">0</p>
              <p>CASH</p>
            </div>
          </div>
        </div>
        <SDialogFooter>
          <div class="d-flex flex-row fl-s-a calculator-footer w-full">
            <SButton
              type="button"
              variant="outline"
              class="cancel"
              data-cy="price-calculator-cancel-button"
              @click="cancel()"
            >
              Cancel
            </SButton>
            <SButton
              type="button"
              class="confirm"
              data-cy="price-calculator-confirm-button"
              @click="$emit('confirmNumber', Number(inputContent))"
            >
              Confirm
            </SButton>
          </div>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
// import ModalModule from '@/store/modules/modal';
import { ModalComponents, ModalStatus } from "@/constants/modal";
import BookingModule from "@/store/modules/booking";
import ModalModule from "@/store/modules/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { defineComponent, PropType, ref } from "vue";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: ModalComponents.Calculator,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
  },
  // props: {
  //   placeholderValue: {
  //     required: false,
  //     type: Object as PropType<number>,
  //   },
  // },
  props: {
    visible: {
      required: false,
      type: Object as PropType<number>,
    },
    placeHolderValue: {
      required: false,
      type: Object as PropType<number>,
    },
  },
  emits: [
    "confirmNumber" /* this event is declared in the parent component Modal*/,
    "cancelCalculator",
  ],

  setup(props) {
    // the calculator is now usable as a standalone component and also from Modal component --> Make the calculator used without Modal component
    let dialogVisible = ref(
      props.visible
        ? props.visible
        : dialogVisibleForStatus(ModalStatus.Calculator)
    );
    let inputContent = props.placeHolderValue
      ? ref(props.placeHolderValue.toString())
      : ref("0");
    let decimal: "." | "," = "." || ",";
    let currentCurrency = ref("CHF");
    let el = ref(null);

    function clearInput() {
      inputContent.value = "0";
    }

    function eraseFromInput() {
      // Removes one number
      // if it is the only number then we replace it with 0
      // if it is '0' then we do nothing

      let length = inputContent.value.length;

      if (inputContent.value.length === 1) {
        if (inputContent.value[length - 1] !== "0") {
          let newInput = inputContent.value.replace(
            inputContent.value[length - 1],
            "0"
          );
          inputContent.value = newInput;
          return;
        }
        if (inputContent.value[length - 1] === "0") {
          return;
        }
      }

      inputContent.value = inputContent.value.slice(0, length - 1);
    }

    function updateInput(event: Event) {
      let char = (event.target as HTMLInputElement).textContent || "";
      if (inputContent.value === "0") {
        inputContent.value = char;
        return;
      }
      inputContent.value = inputContent.value + char;
    }

    function cancel() {
      // Clear the calculator and close it
      clearInput();
      if (ModalModule.status === ModalStatus.Calculator) {
        ModalModule.setStatus(ModalStatus.Hidden);
      } else {
        // this is the case where the calculator is not used inside Modal component, lets emit to cancellation event on parent component
        this.$emit("cancelCalculator");
      }
    }

    return {
      dialogVisible,
      inputContent,
      decimal,
      currentCurrency,
      el,
      clearInput,
      eraseFromInput,
      updateInput,
      cancel,
      props,
    };
  },
  methods: {},
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

$btns-height: 50px;
.calculator {
  .input-screen {
    background-color: $color-light-grey3;
    border-width: 1px;
    border-style: solid;
    border-color: white;
    border-radius: 4px;
    height: 58px;
    line-height: 58px;
    text-align: center;
    margin-bottom: 20px;
    font-weight: 400;
    font-style: normal;
    font-size: 20px;
    text-align: center;
    letter-spacing: 0px;
    color: $color-font-grey1;

    .currency {
      margin-right: 10px;
    }
  }

  .calculator-buttons-wrapper {
    height: fit-content;
    width: 390px;

    p {
      background-color: $color-green;
      border: 1px solid white;
      border-radius: 4px;
      width: 130px;
      height: $btns-height;
      font-family: Arial, sans-serif, Arial;
      font-weight: 700;
      font-style: normal;
      font-size: 20px;
      text-align: center;
      line-height: $btns-height;
      letter-spacing: 0px;
      color: white;
      cursor: pointer;
    }
  }

  .calculator-footer {
    .cancel,
    .confirm {
      font-weight: 400;
      font-style: normal;
      font-size: 14px;
      text-align: center;
      line-height: 17px;
      letter-spacing: 0px;
      height: 51px;
      width: 165px;
    }
    .confirm {
      border-width: 0px;
      border-style: solid;
      border-color: $color-light-grey5;
      border-radius: 4px;
      color: white;
    }
    .cancel {
      color: $color-font-grey1;
      background-color: white;
    }
  }
}
</style>
